import Country, { CountryIsoCode } from '@core/types/country';
import { Amount } from '@core/types/amount';
import { AkLocale, LanguageIsoCode } from '@core/types/base';
import { AlgoliaHit } from 'instantsearch.js';
import { ShapeProperties } from '@monolith/legacy/types/product-shape';
import { GetOneCartStatusEnum } from '@client/api-internal/data-contracts';
import { UploadedFile } from '@monolith/legacy/services/api/upload-file';
import { Currency } from 'dinero.js';
import { Brand } from './api/brand';

export enum StorageTemperatureEnum {
  None = 'none',
  Frozen = 'frozen', // <= -18°C
  UltraFreshStrictChilled = 'ultra_fresh_strict_chilled', // 0 - 2°C
  UltraFreshHighlyPerishable = 'ultra_fresh_highly_perishable', // 0 - 4°C
  Fresh = 'fresh', // 2 - 8°C
  Controlled = 'controlled', // 15 - 25°C
}

export interface Category {
  id: number;
  parent_id: number;
  order: number;
  active: number;
  new: number;
  tree: string;
  name: Record<LanguageIsoCode, string>;
  path: string;
}

export default interface Product {
  type: string;
  id: number;
  uuid?: string;
  link: string;
  created_at: string;
  name: string;
  categories: Category[];
  unit_multiplier: number;
  retail_price: Amount;
  original_wholesale_price: Amount;
  images: string[];
  description: string;
  description_auto_translate: boolean;
  description_original_content: string;
  original_language: string;
  original_language_label: string;
  discount_type?: DiscountType | null;
  brand: Brand;
  out_of_stock?: boolean;
  properties: Property[];
  tags: string[] | Record<AkLocale, string[]>;
  made_in: Country | null;
  options: Option[];
  variants?: Variant[];
  requires_category_update?: boolean;
  qualification?: QualificationEnum;
  discount_rate?: number;
  wholesale_price?: Amount;
  is_best_seller?: boolean;
  is_new?: boolean;
  refreshed?: boolean;
  is_discounted: boolean;
  is_discount_editable: boolean;
  hs_code: string;
  vat_rate: number;
  product_type_id: number;
  attributes: ProductAttribute[];
  has_pending_draft?: boolean;
  isChecked?: boolean;
  available_at?: number;
  active?: boolean;
}

export interface VariantPrice {
  currency: Currency;
  original_wholesale_price: Amount;
  retail_price: Amount;
  wholesale_price?: Amount;
  discount_rate?: number;
}
interface BaseVariantOption {
  id: number;
  name: VariantType;
  display_name: string;
}
export interface VariantOption extends BaseVariantOption {
  value: string;
}
export interface VariantConfig extends BaseVariantOption {
  placeholder: string;
  presets?: VariantPresetConfig[];
}
interface VariantPresetConfig {
  label: string;
  value: string[];
}

enum VariantAvailabilityEnum {
  AVAILABLE_NOW = 'available_now',
}

export enum QualificationEnum {
  NORMAL = 'normal',
  FAST = 'fast',
}

export type Variant = {
  id: number;
  uuid: string;
  sku: string;
  ian?: string;
  name: string;
  out_of_stock?: boolean;
  stock: Stock;
  price: VariantPrice;
  options: VariantOption[];
  created_at: string;
  unit_multiplier: number;
  shape_properties: ShapeProperties;
  images?: string[];
  index?: number;
  fulfillableId?: string;
  available_at?: string;
  is_sellable_as_classic_product?: boolean;
  availability?: VariantAvailabilityEnum;
  wholesale_price: Amount;
  retail_price: Amount;
  estimatedDelivery?: {
    minDays: number | null;
    maxDays: number | null;
    qualification?: QualificationEnum;
  };
};

export interface Stock {
  id: number;
  stock_quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  is_always_in_stock: boolean;
  inventory_policy?: 'deny' | 'continue';
  status: GetOneCartStatusEnum;
}

export interface ProductDiscount {
  origin: string;
  percentage: number;
}

export interface PriceBreakdown {
  originalPrice: Amount;
  finalPrice: Amount;
  wholesalePrice: Amount;
  discounts: Record<string, Amount>;
  retailPrice: Amount;
}

export interface Option {
  type: string;
  id: number;
  name: string;
  sku: string;
  unit_multiplier: number;
  created_at: string;
  variation?: Variation;
  ian: string;
  stock?: Stock;
  out_of_stock?: boolean;
  product_variant_id?: number;
}

export interface Property {
  name: string;
  key: string;
  value: string;
  original_content: string;
  auto_translate: boolean;
}

export interface ProductAttribute {
  name: string;
  value: string;
}

export interface PropertyWithFormat {
  name: string;
  key: string;
  original_content: string;
  type: string;
  placeholder?: string;
  category?: number[] | undefined;
}

export enum DiscountType {
  BRAND = 'brand_discount',
  EVENT_SPECIAL = 'event_special_discount',
  LOYALTY = 'loyalty_discount',
}

export type ProductHitBrand = Pick<
  Brand,
  'id' | 'name' | 'link' | 'minimum' | 'opened_countries' | 'images' | 'type' | 'brand_tags' | 'uuid'
> & {
  country_code: CountryIsoCode;
  country: string;
  business_events: Record<
    number,
    {
      id: number;
      discount: number;
      ranking: number;
    }
  >;
};

export type ProductHit = AlgoliaHit<{
  id: number;
  retail_price: Amount;
  wholesale_price: Amount;
  original_wholesale_price: Amount;
  discount_rate: number;
  discount_type?: DiscountType | null;
  unit_multiplier: number;
  tags: Record<LanguageIsoCode, string[]> | string[];
  link: string;
  name: Record<LanguageIsoCode, string> | string;
  display_name: Record<LanguageIsoCode, string> | string;
  description: Record<LanguageIsoCode, string> | string;
  brand: ProductHitBrand;
  options: Option[];
  variants: Variant[];
  images: string[];
  objectID: string;
  aardvarkServeId?: string;
  available_at?: string;
}>;

export enum VariantType {
  COLOR = 'color',
  SIZE = 'size',
  OTHER = 'other',
}

interface Variation {
  name: string;
}

export const isProduct = (product: unknown): product is Product => {
  return (product as Product)?.type === 'product';
};

export type EditedProductImage = {
  filename: ProductImage;
  order: number;
};

type ProductImageUrl = string;
type ProductImage = ProductImageUrl | UploadedFile;
export enum VariantLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}
